import React from 'react';
import styled from 'styled-components';
import { Field, FieldProps, FieldRenderProps } from 'react-final-form';
import { Calendar } from 'primereact/calendar';
import { Typography } from '../Typography';
import { InfoTooltip } from '../Tooltip/InfoTooltip';

interface FormDateProps extends FieldProps<Date, FieldRenderProps<Date>> {
  id?: string;
  label?: string;
  placeholder?: string;
  tooltip?: React.ReactNode;
  initialValue?: Date;
  required?: boolean;

  // NEW - Props to control allowed dates
  allowPast?: boolean;
  allowFuture?: boolean;

  // UX
  clearable?: boolean;        // shows Clear button in calendar popover
  readOnlyInput?: boolean;    // prevents manual typing (avoids buggy "auto-set" on erase)
}

export const FormDateRelease: React.FC<FormDateProps> = props => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Dynamic min/max logic
  const minDate = props.allowPast ? undefined : today;     // disallow past dates
  const maxDate = props.allowFuture ? undefined : today;   // disallow future dates

  return (
    <Field<Date>
      type="date"
      {...props}
      initialValue={props.initialValue}
      parse={(value: any) => (value == null || value === '' ? null : value)}
      render={({ input }) => (
        <Container id={props.id}>
          <Label>
            <Typography variant="body6">
              <label htmlFor={props.name}>
                {props.label} {props.required && <span style={{ color: '#6777FF' }}>*</span>}
              </label>
            </Typography>

            {props.tooltip && (
              <InfoTooltip
                name={`date-tooltip-${props.name}`}
                tooltip={props.tooltip}
              />
            )}
          </Label>

          <StyledCalendar
            {...input}
            value={input.value ?? null}
            onChange={(e: any) => {
              let next = e?.value ?? null;
              if (Array.isArray(next)) next = next[0] ?? null;
              input.onChange(next ?? null);
            }}
            showIcon
            showButtonBar={!!props.clearable}
            readOnlyInput={!!props.readOnlyInput}
            placeholder={props.placeholder}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Container>
      )}
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledCalendar = styled(Calendar)`
  border-radius: 10px;

  .p-datepicker-trigger {
    background-color: ${({ theme }) => theme.palette.button.primary};
  }
`;

const Label = styled.div`
  display: flex;
  gap: 4px;
`;
